import { useTranslation } from "react-i18next";
import type { Permission } from "@/core/permissions/config";
import { PERMISSIONS } from "@/core/permissions/config";
import { IconName } from "./icons";

export interface MenuSubItem {
  icon: IconName;
  link: string;
  name: string;
  permission: boolean;
  /** true ise yalnızca eğitim yöneticisi menüde görür */
  educationManagerOnly?: boolean;
  /** true ise eğitmen rolündeki kullanıcı menüde görmez */
  hideForTrainer?: boolean;
}

export interface MenuItem {
  icon: IconName;
  link: string;
  name: string;
  permission: boolean;
  submenu?: MenuSubItem[];
  /** true ise yalnızca eğitim yöneticisi menüde görür */
  educationManagerOnly?: boolean;
  /** true ise eğitmen rolündeki kullanıcı menüde görmez */
  hideForTrainer?: boolean;
}

/** Client (kullanıcı) menü öğeleri - sadece mevcut sayfalarla */
export const CLIENT_MENU_ITEMS = (
  hasPermission?: (p: Permission) => boolean,
): MenuItem[] => {
  const { t } = useTranslation();
  const check = (p: Permission) => (hasPermission ? hasPermission(p) : true);

  return [
    {
      icon: "home",
      link: "/dashboard/client-dashboard",
      name: t("clientDashboard.menuTitle"),
      permission: true,
    },
    {
      icon: "educationCatalog",
      link: "/dashboard/education-catalog",
      name: t("educationCatalog.title"),
      permission: true,
    },
    {
      icon: "educationCatalog",
      link: "/dashboard/client-educations",
      name: t("clientEducations.title"),
      permission: true,
    },
    {
      icon: "educationCatalog",
      link: "/dashboard/client-education-requests",
      name: t("clientEducationRequests.title"),
      permission: true,
    },
    {
      icon: "examManagement",
      link: "/dashboard/client-exams",
      name: t("clientExams.title"),
      permission: true,
    },
    {
      icon: "examManagement",
      link: "/dashboard/client-certificates",
      name: t("clientCertificates.title"),
      permission: true,
    },
    {
      icon: "examManagement",
      link: "/dashboard/client-polls",
      name: t("clientPolls.title"),
      permission: true,
    },
    {
      icon: "communicationFeedback",
      link: "/dashboard/client-support-tickets",
      name: t("clientSupportTickets.title"),
      permission: check(PERMISSIONS.SUPPORT_TICKETS_PAGE),
    },
    {
      icon: "announcement",
      link: "/dashboard/client-announcements",
      name: t("clientAnnouncements.title"),
      permission: true,
    },

    {
      icon: "library",
      link: "/dashboard/library",
      name: t("library.title"),
      permission: true,
    },
  ];
};

/** Admin (yönetici) menü öğeleri - PAGE yetkisi yoksa menüde gösterilmez */
export const ADMIN_MENU_ITEMS = (
  hasPermission?: (p: Permission) => boolean,
): MenuItem[] => {
  const { t } = useTranslation();
  const check = (p: Permission) => (hasPermission ? hasPermission(p) : true);

  return [
    {
      icon: "home",
      link: "/dashboard",
      name: t("common.home"),
      permission: true,
    },
    {
      icon: "educationManagement",
      link: "/dashboard/education-management",
      name: t("educationManagement.title"),
      permission: check(PERMISSIONS.EDUCATION_PAGE),
    },
    {
      icon: "educationCatalog",
      link: "/dashboard/request-and-approval",
      name: t("requestAndApproval.title"),
      permission: check(PERMISSIONS.REQUEST_AND_APPROVAL_PAGE),
      hideForTrainer: true,
    },
    {
      icon: "calendar",
      link: "/dashboard/education-calendar",
      name: t("educationCalendar.menuTitle"),
      permission: check(PERMISSIONS.EDUCATION_PAGE),
      educationManagerOnly: true,
    },
    {
      icon: "examManagement",
      link: "/dashboard/exam-management",
      name: t("examManagement.title"),
      permission: check(PERMISSIONS.EXAMS_PAGE),
    },
    {
      icon: "examManagement",
      link: "/dashboard/polls",
      name: t("polls.title"),
      permission: check(PERMISSIONS.POLLS_PAGE),
    },
    {
      icon: "assetCategory",
      link: "/dashboard/categories",
      name: t("categories.title"),
      permission: check(PERMISSIONS.CATEGORIES_PAGE),
    },
    {
      icon: "employeeManagement",
      link: "/dashboard/departments",
      name: t("departments.title"),
      permission: check(PERMISSIONS.DEPARTMENTS_PAGE),
    },
    {
      icon: "department",
      link: "/dashboard/locations",
      name: t("locations.title"),
      permission: check(PERMISSIONS.LOCATIONS_PAGE),
    },
    {
      icon: "communicationFeedback",
      link: "/dashboard/mail-broadcast",
      name: t("mailBroadcast.title"),
      permission: check(PERMISSIONS.EMAIL_BROADCAST_PAGE),
    },
    {
      icon: "employeeManagement",
      link: "/dashboard/staff-management",
      name: t("staffManagement.title"),
      permission: check(PERMISSIONS.STAFF_PAGE),
    },
    {
      icon: "employees",
      link: "/dashboard/black-list",
      name: t("blackList.title"),
      permission: check(PERMISSIONS.BLACK_LIST_PAGE),
    },
    {
      icon: "assignments",
      link: "/dashboard/roles-and-permissions",
      name: t("rolesAndPermissions.title"),
      permission: check(PERMISSIONS.ROLES_AND_PERMISSIONS_PAGE),
    },
    {
      icon: "examManagement",
      link: "/dashboard/certificates",
      name: t("certificates.title"),
      permission: check(PERMISSIONS.CERTIFICATES_PAGE),
    },
    {
      icon: "employees",
      link: "/dashboard/user-groups",
      name: t("userGroups.title"),
      permission: check(PERMISSIONS.USER_GROUPS_PAGE),
    },
    {
      icon: "examManagement",
      link: "/dashboard/intern-management",
      name: t("internManagement.title"),
      permission: check(PERMISSIONS.INTERN_PAGE),
    },
    {
      icon: "announcement",
      link: "/dashboard/announcements",
      name: t("announcements.title"),
      permission: check(PERMISSIONS.ANNOUNCEMENTS_PAGE),
    },
    {
      icon: "reporting",
      link: "/dashboard/reports",
      name: t("reports.title"),
      permission: check(PERMISSIONS.REPORTS_PAGE),
    },
    {
      icon: "logs",
      link: "/dashboard/logs",
      name: t("logs.title"),
      permission: check(PERMISSIONS.LOGS_PAGE),
    },
    {
      icon: "communicationFeedback",
      link: "/dashboard/support-tickets",
      name: t("supportTickets.title"),
      permission: check(PERMISSIONS.SUPPORT_TICKETS_PAGE),
    },
    {
      icon: "library",
      link: "/dashboard/admin-library",
      name: t("library.title"),
      permission: check(PERMISSIONS.LIBRARY_PAGE),
    },
  ];
};

/** Rol bazlı menü döndürür */
export const getMenuItems =
  (
    isStaff: boolean,
    hasPermission?: (p: Permission) => boolean,
  ): (() => MenuItem[]) =>
  () =>
    isStaff ? ADMIN_MENU_ITEMS(hasPermission) : CLIENT_MENU_ITEMS(hasPermission);
